/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {HttpResponse} from '@angular/common/http';
import {SafeHtml, SafeUrl} from '@angular/platform-browser';

import {from, Observable} from 'rxjs';
import {take} from 'rxjs/operators';

import {CSV} from './CSV';

import {isArray} from 'util';
import {User, UserService} from '../user';
import {SignatureData, SignatureInfo, SignatureLocation} from '../files/files.model';

export const getFileNameAndExtension = (name: string) => {
  const [, fileName, fileExtension] = name?.match(/([^\\]*)\.(\w+)$/) || ['', name, ''];
  return {fileName, fileExtension};
};

export const getFormatByMIME = (mime: string) => {
  let format: string;
  switch (mime) {
    case 'application/x-rar-compressed':
      format = 'rar';
      break;
    case 'application/pdf':
      format = 'pdf';
      break;
    case 'application/zip':
      format = 'zip';
      break;
    case 'application/xml':
      format = 'xml';
      break;
    case 'application/xhtml+xml':
      format = 'xhtml';
      break;
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      format = 'docx';
      break;
    case 'application/mswordt':
      format = 'doc';
      break;
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      format = 'xlsx';
      break;
    case 'application/vnd.ms-excel':
      format = 'xls';
      break;
    case 'text/plain':
      format = 'txt';
      break;
    case 'application/rtf':
      format = 'rtf';
      break;
    case 'image/gif':
      format = 'gif';
      break;
    case 'image/jpeg':
      format = 'jpeg';
      break;
    case 'image/png':
      format = 'png';
      break;
    case 'image/webp':
      format = 'webp';
      break;
    case 'text/csv':
      format = 'csv';
      break;
    case 'text/dsn':
      format = 'dsn';
      break;
    default:
      format = '';
      break;
  }

  return format;
};

const normalizeExtension = (extension: string): string => {
  return extension === 'jpg' ? 'jpeg' : extension;
};

export const isValidFormat = (mime: string, file: string): boolean => {
  const {fileExtension} = getFileNameAndExtension(file);
  const expectedExt = normalizeExtension(fileExtension.toLowerCase());

  if (!mime) {
    return false;
  }

  const mimeList = mime.split(',')
    .map(m => m.trim())
    .filter(Boolean);

  return mimeList.some(m => getFormatByMIME(m) === expectedExt);
};

export interface Preview {
  iframe: SafeHtml;
  href: SafeUrl;
  download: string;
}

export interface FileModel {
  blob: Blob;
  type?: string;
  fileName: string;
  signatureData?: SignatureData;
}

export const mapToFileModel = () => (response: HttpResponse<Blob>) => {
  const type: string = response.body.type;
  const blob: Blob = new Blob([response.body], {type});
  const contentDisposition: string = response.headers.get('Content-Disposition');
  const fileName: string = Files.getFileNameFromContentDisposition(contentDisposition);
  const signatureDataHeader: string = response.headers.get('X-Signature-Images');
  const signatureLocations: SignatureLocation[] = signatureDataHeader ? JSON.parse(signatureDataHeader) : null;
  const signatureInfos: SignatureInfo[] = signatureLocations
    ? signatureLocations.map((location: SignatureLocation) => ({
      markerName: location.id,
      signingFieldPlacement: {
        page: location.page,
        left: location.left,
        top: location.top,
        width: location.width,
        height: location.height
      },
      ...(location.signatureMethods?.length ? {signatureMethods: location.signatureMethods} : {}),
      ...(location.signingOrder != null ? {signer: {signingOrder: location.signingOrder}} : {})
    }))
    : null;

  return <FileModel>{blob, type, fileName, ...(signatureInfos ? {signatureData: {signatureInfos}} : {})};
};

/**
 * File utils
 */
export class Files {

  /**
   * Convert dataURI string to blob
   */
  public static dataURItoBlob(dataURI: string): Blob {
    const binary = atob(dataURI.split(',')[1]);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], {type: 'image/png'});
  }

  public static exportToCSV(data: any[] = [],
                            columns: Array<{ name: string, field: string }>,
                            fileName: string = 'export',
                            userService: UserService) {
    let csvSeparator: string = ',';
    userService.getUser().pipe(
      take(1)
    ).subscribe((user: User) => {
      if (user.decimalMask === ',') {
        csvSeparator = ';';
        data.forEach(tableData => {
          Object.keys(tableData).forEach(key => {
            if (typeof tableData[key] === 'number') {
              tableData[key] = tableData[key].toString().replace('.', ',');
            }
          });
        });
      }

      if (!isArray(data) || !data.length) {
        return;
      }

      // const headers: string = columns.map((column: { name: string, field: string }) => column.name)
      //   .join(CSV_ColumnDelimiter) + CSV_LineDelimiter;
      //
      // const body: string = data.map((item: any) =>
      //   columns.map((column: { name: string, field: string }) => item[column.field]).join(CSV_ColumnDelimiter)
      // ).join(CSV_LineDelimiter);

      const headers: Array<string[]> = [columns.map((column: { name: string, field: string }) => column.name)];
      const body: Array<string[]> = data.map((item: any) =>
        columns.map((column: { name: string, field: string }) => item[column.field])
      );

      const blob: Blob = new Blob([`\uFEFF${CSV.fromArrays(headers.concat(body), {separator: csvSeparator})}`], {
        type: `data:text/csv;charset=utf-8`
      });

      const a = document.createElement('a');
      const name = `${fileName}.csv`;

      if (a.download !== void 0) {
        const url = URL.createObjectURL(blob);

        a.style.display = 'none';
        a.setAttribute('href', url);
        a.setAttribute('download', name);
        document.body.appendChild(a);
        a.dispatchEvent(new MouseEvent('click'));
        document.body.removeChild(a);

        setTimeout(function () {
          URL.revokeObjectURL(url);
        }, 100);

      } else if ((navigator as any).msSaveOrOpenBlob) { // IE10+
        (navigator as any).msSaveOrOpenBlob(blob, name);
      }
    });
  }

  public static loadBase64(src: string, fileName: string = 'file') {
    const name = `${fileName}.jpeg`;
    const a = document.createElement('a');

    if (a.download !== void 0) {
      a.style.display = 'none';
      a.setAttribute('href', src);
      a.setAttribute('download', name);
      document.body.appendChild(a);
      a.dispatchEvent(new MouseEvent('click'));
      document.body.removeChild(a);

      setTimeout(function () {
        URL.revokeObjectURL(src);
      }, 100);

    } else if ((navigator as any).msSaveOrOpenBlob) { // IE10+
      const decodedData = atob(src.split(',')[1]);
      const charArray = [];
      for (let i = 0; i < decodedData.length; i++) {
        charArray.push(decodedData.charCodeAt(i));
      }
      const blob = new Blob([new Uint8Array(charArray)], {type: 'image/png'});
      (navigator as any).msSaveOrOpenBlob(blob, name);
    }
  }

  public static blobToBase64(blob): Observable<unknown> {
    return from(new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }));
  }

  public static downloadBlob(blob: Blob, filename: string = 'file', isBlank: boolean = false): void {
    if ((navigator as any).msSaveOrOpenBlob) {
      this.msDownloadBlob(blob, filename);
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.setAttribute('href', url);
      if (isBlank) {
        a.setAttribute('target', '_blank');
      } else {
        a.setAttribute('download', filename);
      }
      document.body.appendChild(a);
      a.dispatchEvent(new MouseEvent('click'));
      document.body.removeChild(a);
    }
  }

  public static msDownloadBlob(blob: Blob, filename: string) {
    (navigator as any).msSaveOrOpenBlob(blob, `${filename}.${getFormatByMIME(blob.type)}`);
  }

  public static downloadAsJSON(obj: any) {
    const a = document.createElement('a');
    const file = new Blob([JSON.stringify(obj)], {type: 'application/json'});
    a.setAttribute('href', URL.createObjectURL(file));
    a.setAttribute('download', 'file');
    a.click();
  }

  public static getFileNameFromContentDisposition(contentDisposition: string) {
    if (contentDisposition) {
      const regex = /.*filename[^;\n=]*=[\'\"]?([^\"]+)/;
      let fileName;
      let result;
      if ((result = regex.exec(contentDisposition)) !== null) {
        result.forEach((match) => {
          fileName = match;
        });
      }
      return fileName;
    }
  }

}
